import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DIRECTIONS, THEMES, TRANSLATIONS } from "@/lib/i18n";
import { WORKSPACE_DARK, WORKSPACE_LIGHT } from "@/lib/workspaceTheme";

const Ctx = createContext(null);

const LS_LANG = "vta_lang";
const LS_THEME = "vta_theme";

function pickInitial(key, fallback, valid) {
  try {
    const v = localStorage.getItem(key);
    if (v && valid.includes(v)) return v;
  } catch { /* ignore */ }
  return fallback;
}

export function I18nThemeProvider({ children }) {
  const [lang, setLangState] = useState(() => pickInitial(LS_LANG, "ar", ["ar", "en", "ur"]));
  const [themeName, setThemeNameState] = useState(() => pickInitial(LS_THEME, "dark", ["dark", "light"]));
  // Workspace mode — activated by <AppShell> and by AppShell alone. When true,
  // the theme is swapped to the deep-navy workspace palette so the internal
  // UI feels distinct from the public portal (which keeps the original theme).
  const [workspaceMode, setWorkspaceMode] = useState(false);

  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem(LS_LANG, next); } catch { /* ignore */ }
  }, []);
  // Cycle: ar → en → ur → ar
  const toggleLang = useCallback(() => {
    const cycle = { ar: "en", en: "ur", ur: "ar" };
    setLang(cycle[lang] || "ar");
  }, [lang, setLang]);

  const setTheme = useCallback((next) => {
    setThemeNameState(next);
    try { localStorage.setItem(LS_THEME, next); } catch { /* ignore */ }
  }, []);
  const toggleTheme = useCallback(
    () => setTheme(themeName === "dark" ? "light" : "dark"),
    [themeName, setTheme]
  );

  const baseTheme = THEMES[themeName];
  const dir = DIRECTIONS[lang];

  // Brand identity colours are LOCKED to the original hard-coded palette on
  // the public portal (dark navy + gold). Inside the internal workspace we
  // upgrade to the new deep-navy palette (WORKSPACE_DARK). Light-mode is
  // suppressed inside the workspace per the redesign brief — the toggle
  // stays in code but the AppShell hides its button.
  const theme = workspaceMode
    ? (themeName === "light" ? WORKSPACE_LIGHT : WORKSPACE_DARK)
    : baseTheme;

  // Apply globally to <html> & <body>
  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    document.body.style.background = theme.bg;
    document.body.style.color = theme.text;
    document.body.style.transition = "background-color 200ms ease, color 200ms ease";
  }, [dir, lang, theme]);

  const t = useCallback(
    (key, vars) => {
      let s = (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) || TRANSLATIONS.ar[key] || key;
      if (vars) {
        Object.keys(vars).forEach((k) => {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]);
        });
      }
      return s;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang, dir, t, setLang, toggleLang, theme, themeName, setTheme, toggleTheme,
      workspaceMode, setWorkspaceMode,
    }),
    [lang, dir, t, setLang, toggleLang, theme, themeName, setTheme, toggleTheme, workspaceMode]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n must be inside I18nThemeProvider");
  return v;
};
